import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { FieldTip } from '@/components/FieldTip.js';

type AiConfigItem = {
  task: string;
  provider: string;
  model: string;
  temperature: number;
  max_tokens: number;
};

type AiConfigResponse = {
  tasks: string[];
  providers: string[];
  items: AiConfigItem[];
};

export function AiTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin-ai'],
    queryFn: async () => (await api.get<AiConfigResponse>('/admin/ai/config')).data,
  });

  const update = useMutation({
    mutationFn: async ({ task, payload }: { task: string; payload: Partial<AiConfigItem> }) =>
      (await api.put(`/admin/ai/config/${task}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-ai'] }),
  });

  const indexRag = useMutation({
    mutationFn: async () =>
      (await api.post('/ai/index')).data as { chunks: number; sources: Record<string, number> },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-corpus-stats'] }),
  });

  const corpusStats = useQuery({
    queryKey: ['ai-corpus-stats'],
    queryFn: async () =>
      (await api.get<{ total: number; by_type: Record<string, number> }>('/ai/corpus/stats')).data,
  });

  if (q.isLoading) return <p className="text-ha-muted">Carregando…</p>;

  return (
    <div className="space-y-6">
      <section className="rounded-card border border-ha-border2 bg-ha-card p-4">
        <h3 className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-ha-muted">
          Corpus RAG (M4)
        </h3>
        <p className="mb-3 text-sm text-ha-muted">
          Indexa método 5 Forças + trilhas/aulas para o assistente responder a partir do corpus.
        </p>
        {corpusStats.data ? (
          <p className="mb-3 text-xs text-ha-subtle">
            Chunks: {corpusStats.data.total}
            {Object.keys(corpusStats.data.by_type).length
              ? ` (${Object.entries(corpusStats.data.by_type)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(', ')})`
              : ''}
          </p>
        ) : null}
        <button
          type="button"
          disabled={indexRag.isPending}
          className="rounded-element border border-ha-border2 px-4 py-2 text-[10px] font-bold uppercase text-ha-subtle hover:border-accent"
          onClick={() => indexRag.mutate()}
        >
          {indexRag.isPending ? 'Indexando…' : 'Reindexar corpus'}
        </button>
        {indexRag.isSuccess ? (
          <p className="mt-2 text-xs text-primary">OK — {indexRag.data.chunks} chunks.</p>
        ) : null}
        {indexRag.isError ? (
          <p className="mt-2 text-xs text-feedback">{extractError(indexRag.error).message}</p>
        ) : null}
      </section>
      <p className="text-sm text-ha-muted">
        Configuração de modelos por tarefa (GROQ padrão). Chaves de API via variáveis de ambiente no servidor.
      </p>
      {q.data?.items.map((item) => (
        <AiRow
          key={item.task}
          item={item}
          providers={q.data!.providers}
          onSave={(p) => update.mutate({ task: item.task, payload: p })}
          error={update.error ? extractError(update.error).message : null}
        />
      ))}
    </div>
  );
}

function AiRow({
  item,
  providers,
  onSave,
  error,
}: {
  item: AiConfigItem;
  providers: string[];
  onSave: (p: Partial<AiConfigItem>) => void;
  error: string | null;
}) {
  const [provider, setProvider] = useState(item.provider);
  const [model, setModel] = useState(item.model);
  const [temperature, setTemperature] = useState(String(item.temperature));
  const [maxTokens, setMaxTokens] = useState(String(item.max_tokens));

  return (
    <fieldset className="border border-ha-border bg-ha-card2 p-4">
      <legend className="px-1 text-xs font-bold uppercase text-accent">{item.task}</legend>
      <div className="grid gap-3 md:grid-cols-2">
        <FieldTip label="Provedor" hint="GROQ, ANTHROPIC ou OPENAI.">
          <select
            className="w-full border border-ha-border2 bg-ha-card px-3 py-2 text-sm"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            {providers.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </FieldTip>
        <FieldTip label="Modelo" hint="Identificador do modelo no provedor.">
          <input
            className="w-full border border-ha-border2 bg-ha-card px-3 py-2 text-sm"
            value={model}
            onChange={(e) => setModel(e.target.value)}
          />
        </FieldTip>
        <FieldTip label="Temperature" hint="0 = determinístico, 2 = mais criativo.">
          <input
            type="number"
            step="0.1"
            min={0}
            max={2}
            className="w-full border border-ha-border2 bg-ha-card px-3 py-2 text-sm"
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
          />
        </FieldTip>
        <FieldTip label="Max tokens" hint="Limite de tokens na resposta.">
          <input
            type="number"
            className="w-full border border-ha-border2 bg-ha-card px-3 py-2 text-sm"
            value={maxTokens}
            onChange={(e) => setMaxTokens(e.target.value)}
          />
        </FieldTip>
      </div>
      {error && <p className="mt-2 text-xs text-feedback">{error}</p>}
      <button
        type="button"
        className="mt-3 bg-accent px-4 py-2 text-xs font-bold text-black"
        onClick={() =>
          onSave({
            provider,
            model,
            temperature: parseFloat(temperature),
            max_tokens: parseInt(maxTokens, 10),
          })
        }
      >
        Guardar
      </button>
    </fieldset>
  );
}
